import * as Icon from 'base/ui/components/Icons';

import { Form, Modal, Tooltip, message } from 'antd';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryMdPresenter, categoryMdStore, userStore } from 'stores/root_store';
import { DATE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-components';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomInput from 'base/ui/components/CustomInput';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { usePermission } from 'hooks/usePermission';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { HiSearch } from 'react-icons/hi';
import { DataCategory } from 'services/RawData/category_services';
import { dropdownStatus } from 'utils/dropdown';
import { removeEmptyValues } from 'utils/transformHelper';

const { confirm } = Modal;

function CategoryComponent() {
  usePermission('INDEX');

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const pagetitle = t('masterdata.category.title');
  document.title = pagetitle + ' - ' + SYSTEM_TITLE;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<DataCategory[]>([]);
  const [dataSearch, setDataSearch] = useState({
    paging: {
      pageIndex: 1,
      pageSize: PAGE_SIZE_DEFAULT,
      orderBy: '',
      orderByDesc: '',
    },
    actived: true as boolean | undefined,
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const showDrawer = () => setOpenDrawer(true);
  const onCloseDrawer = () => setOpenDrawer(false);

  const defaultColumn: ProColumns<DataCategory>[] = [
      {
        title: t('masterdata.category.stt'),
        dataIndex: 'stt',
        width: 30,
        key: 'stt',
        className: '!rounded-none !py-1',
        align: 'center',
        render: (text) => <div className="!text-center">{text}</div>,
        fixed: 'left',
        ellipsis: true,
      },
      {
        title: t('masterdata.category.category-code'),
        dataIndex: 'categoryCode',
        width: 60,
        key: 'categoryCode',
        className: '!rounded-none !py-1',
        fixed: 'left',
        ellipsis: true,
      },
      {
        title: t('masterdata.category.category-name'),
        dataIndex: 'categoryName',
        width: 180,
        key: 'categoryName',
        className: '!rounded-none !py-1',
        ellipsis: true,
      },
      {
        title: t('masterdata.category.status'),
        dataIndex: 'actived',
        width: 50,
        key: 'actived',
        className: '!rounded-none !py-1',
        ellipsis: true,
        render: (_, record) => {
          return (
            <div>
              {record.actived ? (
                <Tooltip title="Đang sử dụng">
                  <Icon.CheckStatus className="w-4 text-sm text-center mx-auto !fill-[#389E0D]" />
                </Tooltip>
              ) : (
                <Tooltip title="Ngưng sử dụng">
                  <Icon.CloseStatus className="w-4 text-sm text-center mx-auto !fill-[#D93133E5]" />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: t('masterdata.category.actions'),
        dataIndex: 'function',
        width: 50,
        key: 'function',
        className: '!rounded-none !py-1',
        align: 'center',
        fixed: 'right',
        render: (_, record) => {
          return (
            <div className="w-full flex items-center justify-center gap-x-1">
              {userStore.userPermission?.includes('EDIT') ? (
                <Tooltip title={t('btn.edit')}>
                  <CustomButton
                    className="!w-6 !h-6 rounded-sm !border-[#4DA8FF] flex gap-1 items-center justify-center !bg-[#C9E8FF] hover:bg-[#95D2FF]"
                    icon={<Icon.PencilEdit className="!fill-[#007BF1]" width="16" height="16" />}
                    onClick={() => navigate(`${record.categoryId}`)}
                  />
                </Tooltip>
              ) : null}
              {userStore.userPermission?.includes('DELETE') ? (
                record.actived ? (
                  <Tooltip title={t('btn.delete')}>
                    <CustomButton
                      className="!w-6 !h-6 rounded-sm !border-[#FF4F51] flex gap-1 items-center justify-center !bg-[#FFE5E6] hover:bg-[#FF8081]"
                      icon={<Icon.Trash className="!fill-[#FC5C5E]" width="16" height="16" />}
                      onClick={() => showDeleteConfirm(record)}
                    />
                  </Tooltip>
                ) : null
              ) : null}
            </div>
          );
        },
      },
  ];

  const [columns, setColumn] = useState<ProColumns<DataCategory>[]>(defaultColumn);
  const handleResize = (index: number) => (e: any, { size }: any) => {
    setColumn((prevColumns) => {
      const nextColumns = [...prevColumns];
      nextColumns[index] = { ...nextColumns[index], width: size.width } as any;
      return nextColumns;
    });
  };

  const getDataSearch = async (payload: any) => {
    await categoryMdPresenter.searchCategory(categoryMdStore, payload);
    if (categoryMdStore.searchResponse.isSuccess) {
      setListData(categoryMdStore.searchResponse.data);
    }
  };

  const handleSearch = async (values: any) => {
    setLoading(true);
    const tempDataSearch = {
      ...values,
      paging: {
        ...dataSearch.paging,
        pageIndex: 1,
      },
    };
    removeEmptyValues(tempDataSearch);
    setDataSearch(tempDataSearch);
    await getDataSearch(tempDataSearch);
    setLoading(false);
    onCloseDrawer();
  };

  const showDeleteConfirm = (item: DataCategory) => {
    confirm({
      title: `Xoá danh mục`,
      icon: <ExclamationCircleFilled />,
      content: `${t('message.question-delete')} "${item.categoryName}"`,
      okText: t('btn.delete'),
      okType: 'danger',
      cancelText: t('btn.cancel'),
      className: 'custom__modalconfirm--delete',
      onOk() {
        handleClickDelete(item.categoryId);
      },
      onCancel() {},
    });
  };

  const handleClickDelete = async (categoryId: string) => {
    setLoading(true);
    if (categoryId) {
      await categoryMdPresenter.deleteCategory(categoryMdStore, categoryId);
      if (categoryMdStore.deleteResponse.isSuccess) {
        message.success(categoryMdStore.deleteResponse.message);
        await getDataSearch(dataSearch as any);
      } else {
        message.error(categoryMdStore.deleteResponse.message);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full">
        <CustomBreadcrumb
          pagetitle={[{ title: pagetitle, link: '/MasterData/Category' }]}
          extra={
            <>
              <CustomButton onClick={showDrawer} type="primary" color="#EFF4F9" className="!px-0 w-8">
                <Icon.Filter className="fill-primary" />
              </CustomButton>
              {userStore.userPermission?.includes('CREATE') ? (
                <CustomButton type="primary" onClick={() => navigate('Create')} icon={<Icon.Plus className="!fill-[#fff]" />}>
                  {t('btn.add')}
                </CustomButton>
              ) : null}
            </>
          }
        />
        <BoxContainer>
          <CustomProTable<DataCategory>
            columns={columns}
            handleResize={handleResize}
            rowKey="categoryId"
            bordered
            search={false}
            loading={loading}
            request={async (params = {}, sort, filter) => {
              setLoading(true);
              const data: any = await getDataSearch({
                ...dataSearch,
                paging: {
                  ...dataSearch.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                },
              });
              if (Object.values(sort).length) {
                setDataSearch({
                  ...dataSearch,
                  paging: {
                    ...dataSearch.paging,
                    orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                    orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  },
                });
              }
              setLoading(false);
              return {
                data: data,
                success: categoryMdStore.searchResponse.isSuccess,
              };
            }}
            dataSource={listData}
            scroll={{ x: 1000, y: 500 }}
            pagination={{
              ...tablePagination(dataSearch as any, setDataSearch as any),
              total: categoryMdStore.searchResponse?.paging?.recordsTotal,
            }}
            options={false}
          />
        </BoxContainer>
        <SearchBox
          className="lg:!w-[35%]"
          footer={[
            <div key="footer" className="flex justify-end">
              <CustomButton
                type="primary"
                icon={<HiSearch />}
                htmlType="submit"
                onClick={() => {
                  form.submit();
                  onCloseDrawer();
                }}
              >
                {t('btn.search')}
              </CustomButton>
            </div>,
          ]}
          onClose={onCloseDrawer}
          open={openDrawer}
        >
          <SearchForm column="col-1" form={form} onFinish={handleSearch}>
            <button hidden type='submit'></button>
            <SearchItem label={'Mã danh mục'} name={'categoryCode'}>
              <CustomInput />
            </SearchItem>
            <SearchItem label={'Tên danh mục'} name={'categoryName'}>
              <CustomInput />
            </SearchItem>
            <SearchItem label={t('masterdata.store.status')} name={'actived'}>
              <CustomSelect options={dropdownStatus} placeholder={t('place-holder.all')} allowClear />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </div>
  );
}

const CategoryPage = () => {
  return observer(CategoryComponent);
};

export default CategoryPage;


